// ---------------------------------------------------------------------------
// Where the API lives. A pure function, so the rule is testable rather than a
// ternary buried in a provider.
//
// The SPA and the Worker are two servers in development (5173 and 8787) and one
// origin in production. Getting this wrong produces the most confusing failure
// in the stack: every request is refused by CORS, nothing is logged server-side
// because the browser never sends it, and every panel on the page shows a dash.
// A whole afternoon went to exactly this on the sibling product, at a port
// number's distance.
// ---------------------------------------------------------------------------

/** wrangler dev's default port. The HOST is taken from the page, never fixed. */
export const DEV_API_PORT = 8787;

/**
 * S0-163 (owner, 15 October 2026). The console signs in on event.clinic's own host, and
 * talks to the SAME API under an event.clinic name, so its session cookie stays same-site
 * and HttpOnly (owner's choice, 16 October). One build serves both hosts, so the page's
 * own origin decides, ahead of VITE_API_BASE.
 */
export const EVENT_CLINIC_CONSOLE = "https://verbunk.event.clinic";
export const EVENT_CLINIC_API = "https://verbunk-api.event.clinic";

/** Only that exact origin offers event.clinic's sign-in (SignIn.tsx). */
export function offersEventClinicSignIn(origin: string): boolean {
  return origin === EVENT_CLINIC_CONSOLE;
}

/**
 * @param configured `VITE_API_BASE`, when someone has set one.
 * @param origin     `window.location.origin`.
 * @returns a base with no trailing slash. `""` means "same origin".
 */
export function resolveApiBase(configured: string | undefined, origin: string): string {
  if (origin.trim() === EVENT_CLINIC_CONSOLE) return EVENT_CLINIC_API;
  const explicit = (configured ?? "").trim();
  if (explicit !== "") return explicit.replace(/\/+$/, "");

  // A development origin gets the development Worker — ON THE SAME HOSTNAME.
  //
  // THIS IS NOT COSMETIC, AND IT WAS FOUND BY OPENING THE PAGE. The first
  // version returned a fixed "http://127.0.0.1:8787" whatever the page's
  // origin. A browser at http://localhost:5173 talking to http://127.0.0.1:8787
  // is CROSS-SITE — `localhost` and `127.0.0.1` are different hosts and neither
  // has a registrable domain — so the session cookie, which is SameSite=Lax,
  // would never have been attached. CORS would have been perfect and every
  // signed-in request would still have arrived anonymous.
  //
  // A different PORT on the same hostname is same-site, so the cookie travels.
  // Taking the host from the page rather than writing one down is what makes
  // that true whichever of the two names the developer typed.
  const match = /^https?:\/\/(localhost|127\.0\.0\.1|\[::1\])(:\d+)?$/.exec(origin.trim());
  if (match) return `http://${match[1]}:${DEV_API_PORT}`;

  // Anything else is served alongside its API, which is what a single Worker
  // deployment looks like — and means the built bundle contains no hard-coded
  // hostname that could point a staging build at production's API.
  return "";
}
